import React from 'react';
import styled from 'styled-components';

const Section = styled.section`
    background-color: ${({ theme }) => theme.colors.backgroundLighter};
    padding: ${({ theme }) => theme.spacing.xxl} ${({ theme }) => theme.spacing.xl};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
    }
`;

const SectionContent = styled.div`
    max-width: 1000px;
    margin: 0 auto;
`;

const SectionTitle = styled.h2`
    font-size: 2.5rem;
    color: ${({ theme }) => theme.colors.primary};
    margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    text-align: center;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 2rem;
    }
`;

const ShowsTable = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    padding-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ShowRow = styled.div`
    display: grid;
    grid-template-columns: 200px 1fr 150px;
    gap: ${({ theme }) => theme.spacing.lg};
    align-items: end;
    padding: ${({ theme }) => theme.spacing.lg} 0;
    position: relative;

    /* Top gradient line */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: linear-gradient(
            to right,
            transparent 0%,
            ${({ theme }) => theme.colors.primary} 50%,
            transparent 100%
        );
        opacity: 0.4;
    }

    /* Bottom gradient line for last item */
    &:last-child::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: linear-gradient(
            to right,
            transparent 0%,
            ${({ theme }) => theme.colors.primary} 50%,
            transparent 100%
        );
        opacity: 0.4;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        grid-template-columns: 1fr;
        gap: ${({ theme }) => theme.spacing.sm};
        padding: ${({ theme }) => theme.spacing.md} 0;
        align-items: start;
    }
`;

const DateColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        align-items: center;
        text-align: center;
    }
`;

const Date = styled.div`
    font-size: ${({ theme }) => theme.fontSizes.large};
    color: ${({ theme }) => theme.colors.primary};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
`;

const Venue = styled.div`
    font-size: ${({ theme }) => theme.fontSizes.medium};
    color: ${({ theme }) => theme.colors.text};
    font-weight: ${({ theme }) => theme.fontWeights.normal};
`;

const LocationColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding-left: ${({ theme }) => theme.spacing.lg};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding-left: 0;
        align-items: center;
        text-align: center;
    }
`;

const Location = styled.div`
    font-size: ${({ theme }) => theme.fontSizes.medium};
    color: ${({ theme }) => theme.colors.primary};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
`;

const TicketsColumn = styled.div`
    display: flex;
    justify-content: flex-end;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        justify-content: center;
    }
`;

const TicketsButton = styled.a`
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.backgroundDark};
    padding: 0.75rem 2rem;
    border-radius: ${({ theme }) => theme.borderRadius.button};
    font-size: ${({ theme }) => theme.fontSizes.medium};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    text-decoration: none;
    text-transform: uppercase;
    transition: background-color 0.2s ease, transform 0.2s ease;
    display: inline-block;
    text-align: center;

    &:hover {
        background-color: ${({ theme }) => theme.colors.primaryDark};
        transform: translateY(-2px);
    }

    &:active {
        transform: translateY(0);
    }
`;

interface Show {
    date: string;
    venue: string;
    location: string;
    ticketsLink: string;
}

interface ShowsSectionProps {
    shows: Show[];
}

export const ShowsSection: React.FC<ShowsSectionProps> = ({ shows }) => {
    return (
        <Section>
            <SectionContent>
                <SectionTitle>Upcoming Shows</SectionTitle>
                <ShowsTable>
                    {shows.map((show, index) => (
                        <ShowRow key={index}>
                            <DateColumn>
                                <Date>{show.date}</Date>
                                <Venue>{show.venue}</Venue>
                            </DateColumn>
                            <LocationColumn>
                                <Location>{show.location}</Location>
                            </LocationColumn>
                            <TicketsColumn>
                                <TicketsButton
                                    href={show.ticketsLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Tickets
                                </TicketsButton>
                            </TicketsColumn>
                        </ShowRow>
                    ))}
                </ShowsTable>
            </SectionContent>
        </Section>
    );
};
